import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Reveal, Seo } from "@/components";
import { Container } from "@/layouts";
import { getUpdate } from "@/data";
import { localizePath, normalizeLanguage, ROUTES, useLocalePath } from "@/navigations";
import { absoluteUrl, articleJsonLd, breadcrumbJsonLd } from "@/utils/seo";

import shared from "@/styles/pages.module.css";

const UpdateDetailPage = memo(() => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation("UpdatesPage");
  const { t: tSeo } = useTranslation("Seo");
  const { t: tc } = useTranslation("components");
  const lang = normalizeLanguage(i18n.language);
  const lp = useLocalePath();

  const update = slug ? getUpdate(slug) : undefined;
  if (!update) {
    return <Navigate to={localizePath(ROUTES.NOT_FOUND, normalizeLanguage(i18n.language))} replace />;
  }

  const title = t(`items.${update.slug}.title`);
  const summary = t(`items.${update.slug}.summary`);
  const body = t(`items.${update.slug}.body`, { returnObjects: true }) as string[];

  const path = `${ROUTES.UPDATES}/${update.slug}`;

  return (
    <article>
      <Seo
        title={tSeo("updateDetail.title", { title })}
        description={tSeo("updateDetail.description", { summary })}
        path={path}
        jsonLd={[
          articleJsonLd(title, summary, absoluteUrl(path, lang), t(`items.${update.slug}.date`)),
          breadcrumbJsonLd(
            [
              { name: tc("nav.home"), path: ROUTES.HOME },
              { name: tc("nav.updates"), path: ROUTES.UPDATES },
              { name: title, path },
            ],
            lang,
          ),
        ]}
      />

      <section className={shared.detailHeader}>
        <Container size="narrow">
          <Link to={lp(ROUTES.UPDATES)} className={shared.backLink}>
            <ArrowLeft size={15} />
            {t("detail.back")}
          </Link>
          <div className={shared.detailMeta}>
            <span className={shared.detailCategory}>{t(`items.${update.slug}.tag`)}</span>
            <span className={shared.detailDate}>{t(`items.${update.slug}.date`)}</span>
          </div>
          <h1 className={shared.detailTitle}>{title}</h1>
          <p className={shared.lede}>{summary}</p>
        </Container>
      </section>

      <section className={shared.sectionTight}>
        <Container size="narrow">
          <Reveal className={shared.prose}>
            {body.map((p, i) => (
              <p key={i} data-reveal>
                {p}
              </p>
            ))}
          </Reveal>
        </Container>
      </section>
    </article>
  );
});

UpdateDetailPage.displayName = "UpdateDetailPage";
export default UpdateDetailPage;
